<script>
import {
  CdrList,
  CdrLink,
  IconXLg,
  IconListRagged,
} from '@rei/cedar';

export default {
  name: 'InPageNav',
  components: {
    CdrList,
    CdrLink,
    IconXLg,
    IconListRagged,
  },
  props: {
    navModel: { type: Array, default: () => [] },
  },
  data() {
    return {
      isMenuOpen: false,
      activeHref: '/about-rei',
    };
  },
  mounted() {
    this.setActiveHref();
    window.addEventListener('click', this.handleOutsideClick);
    window.addEventListener('focusin', this.handleOutsideFocus);
  },
  beforeUnmount() {
    window.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('focusin', this.handleOutsideFocus);
  },
  methods: {
    setActiveHref() {
      this.activeHref = window?.location?.pathname;
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    /**
   * Closes the mobile menu if a click occurs outside of the navigation component.
   */
    handleOutsideClick(event) {
      if (this.isMenuOpen && !this.$refs.inPageNav.contains(event.target)) {
        this.isMenuOpen = false;
      }
    },
    /**
     * Closes the mobile menu if focus moves outside of the navigation component.
     */
    handleOutsideFocus(event) {
      if (this.isMenuOpen && !this.$refs.inPageNav.contains(event.target)) {
        this.isMenuOpen = false;
      }
    },
  },
};
</script>
<template>
  <nav
    id="in-page-nav"
    ref="inPageNav"
    aria-label="Quick links"
    class="in-page-nav"
  >
    <div class="in-page-nav__wrapper">
      <div
        class="in-page-nav__header"
        @click="toggleMenu"
      >
        <component
          :is="isMenuOpen ? 'icon-x-lg' : 'icon-list-ragged'"
          :inherit-color="false"
          class="in-page-nav__icon"
          @click.stop="toggleMenu"
        />
        <h2
          class="in-page-nav__title"
        >
          Explore About REI
        </h2>
      </div>
      <div :class="['in-page-nav__content', { 'is-open': isMenuOpen }]">
        <cdr-list class="in-page-nav__list">
          <li
            v-for="(item, index) in navModel"
            :key="`${item.id}-${index}`"
          >
            <cdr-link
              :id="`nav-${item.id}`"
              :href="item.href"
              :target="item['new-tab'] ? '_blank' : null"
              :class="[
                'in-page-nav__link',
                { 'in-page-nav__link--active': item.href === activeHref },
              ]"
            >
              {{ item.title }}
            </cdr-link>
          </li>
        </cdr-list>
      </div>
    </div>
  </nav>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.in-page-nav {
  padding: 0 0 0 $cdr-space-two-x;

  @include cdr-xs-mq-only {
    padding: 0;
    background-color: $cdr-color-background-primary;
    position: relative;
  }

  @include cdr-sm-mq-up {
    padding-top: $cdr-space-three-x;
    padding-left: $cdr-space-two-x;
    padding-right: 2px; // make up for 2px border right
    overflow: hidden;
    border-right: 2px solid #dcd6cb;
  }

  &__wrapper {
    @include cdr-xs-mq-only {
      padding: $cdr-space-one-x $cdr-space-one-and-a-half-x;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: $cdr-space-one-x;
    background-color: $cdr-color-background-primary;
    border: 1px solid $cdr-color-border-primary;

    @include cdr-sm-mq-up {
      border: none;
      padding: 0;
    }
  }

  &__icon {
    @include cdr-sm-mq-up {
      display: none;
    }
  }

  &__title {
    @include cdr-text-heading-sans-500;
    margin: 0 0 0 $cdr-space-one-x;
    color: $cdr-color-text-primary;

    @include cdr-sm-mq-up {
      margin: 0 0 $cdr-space-one-x 0;
      padding-right: $cdr-space-one-x;
      color: $cdr-color-text-brand;
    }
  }

  &__content {
    @include cdr-xs-mq-only {
      display: none;
      position: absolute;
      left: 24px;
      right: 24px;
      background-color: $cdr-color-background-primary;
      border: 1px solid $cdr-color-border-primary;
      border-top: none;
      z-index: 100;

      &.is-open {
        display: block;
      }
    }
  }

  &__list {
    @include cdr-xs-mq-only {
     padding-top: $cdr-space-one-x;

      a { padding-left: $cdr-space-two-x }
    }

    & li {
      & a {
        width: 100%;
        text-decoration: none;

        @include cdr-sm-mq-up {
          color: $cdr-color-text-primary;
          padding: $cdr-space-one-x $cdr-space-one-x $cdr-space-one-x 0;
        }
      }

      & a:hover {
        color: $cdr-color-text-primary;
        text-decoration: underline;
      }

      & a:active {
        color: $cdr-color-text-brand;
      }

      & a:active,
      a:focus {
        outline-color: #4e4d49;
      }
    }

  }

  &__link {
    display: block;
    padding: $cdr-space-one-x;
    text-decoration: none;
    color: $cdr-color-text-primary;
    position: relative;

    &--active {
      &::after {
        content: '';
        width: 4px;
        height: 100%;
        background: #406eb5;
        position: absolute;
        top: 0;
        right: -1px;
        @include cdr-sm-mq-up {
          right: -2px;
        }
      }
    }
  }
}
</style>
